import type { DaySummary } from '@nutrigeist/shared';
import { formatGrams, formatKcal } from '../lib/format.js';

interface MacroSummaryProps {
  summary: DaySummary;
}

interface MacroBarProps {
  label: string;
  consumed: number;
  goal: number | null;
  color: string;
}

function MacroBar({ label, consumed, goal, color }: MacroBarProps): JSX.Element {
  const percent = goal && goal > 0 ? Math.min(100, (consumed / goal) * 100) : 0;

  return (
    <div className="macro-cell">
      <div className="macro-value">{formatGrams(consumed)} g</div>
      <div className="macro-label">{label}</div>
      <div className="macro-bar">
        <span style={{ width: `${percent}%`, background: color }} />
      </div>
      <div className="hint" style={{ marginTop: 4 }}>
        {goal ? `von ${formatGrams(goal)} g` : ' '}
      </div>
    </div>
  );
}

/**
 * The day's headline: energy against goal, then the three macros.
 *
 * When no calorie goal is set the card shows the plain total instead of a
 * remaining budget, so the app is useful before anything is configured.
 */
export function MacroSummary({ summary }: MacroSummaryProps): JSX.Element {
  const { totals, goals, remaining } = summary;
  const kcalGoal = goals.kcal;
  const kcalPercent = kcalGoal && kcalGoal > 0 ? Math.min(100, (totals.kcal / kcalGoal) * 100) : 0;
  const over = remaining.kcal != null && remaining.kcal < 0;

  return (
    <div className="card">
      <div className="row-between">
        <div>
          <div className="calorie-headline">
            <span className="value">{formatKcal(totals.kcal)}</span>
            <span className="unit">{kcalGoal ? `/ ${formatKcal(kcalGoal)} kcal` : 'kcal'}</span>
          </div>
          {remaining.kcal != null && (
            <div className="hint" style={{ marginTop: 4 }}>
              {over
                ? `${formatKcal(Math.abs(remaining.kcal))} kcal über dem Ziel`
                : `${formatKcal(remaining.kcal)} kcal übrig`}
            </div>
          )}
        </div>

        <span className="badge">
          {summary.entryCount} {summary.entryCount === 1 ? 'Eintrag' : 'Einträge'}
        </span>
      </div>

      {kcalGoal != null && kcalGoal > 0 && (
        <div className="macro-bar" style={{ marginTop: 12, height: 8 }}>
          <span
            style={{
              width: `${kcalPercent}%`,
              background: over ? 'var(--danger)' : 'var(--accent)',
            }}
          />
        </div>
      )}

      <div className="macro-grid">
        <MacroBar
          label="Eiweiß"
          consumed={totals.protein}
          goal={goals.protein}
          color="var(--protein)"
        />
        <MacroBar
          label="Kohlenh."
          consumed={totals.carbs}
          goal={goals.carbs}
          color="var(--carbs)"
        />
        <MacroBar label="Fett" consumed={totals.fat} goal={goals.fat} color="var(--fat)" />
      </div>
    </div>
  );
}
